import {
  Header,
  IconAdd,
  ModalDialog,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import { CreatePODisclaimerLabelFormPresenter } from '@webapp/modules/purchasing/features/create-po-disclaimer-label/presenters/CreatePODisclaimerLabelFormPresenter'
import { EditablePODisclaimerLabelTextFieldPresenter } from '@webapp/modules/purchasing/features/edit-po-disclaimer-label/presenters/EditablePODisclaimerLabelTextFieldPresenter'
import { PODisclaimerLabelListPresenter } from '@webapp/modules/purchasing/features/list-po-disclaimer-labels/presenters/PODisclaimerLabelListPresenter'
import messages from './messages'
import { usePODisclaimerLabelsView } from './usePODisclaimerLabelsView'

/**
 * Feature Set: PO Disclaimer/Labels section (Disclaimer tab)
 *
 * Orchestrates:
 * - Heading/description row + "New label" button (FS composition)
 * - The list feature, with per-row inline-edit cells injected via
 *   renderEditableCells (×2 per row), the default-star bridge, and row
 *   deletes delegated to the hook-only delete command (ADR-9)
 * - The FS-owned create ModalDialog wrapping the create form presenter
 */
export const PODisclaimerLabelsView = () => {
  const intl = useProcurifyIntl()

  const {
    data: { labels, defaultId },
    states: { isCreateModalOpen },
    methods: {
      openCreateModal,
      closeCreateModal,
      onToggleDefault,
      onDeleteLabel,
    },
  } = usePODisclaimerLabelsView()

  const renderEditableCells = (label: PODisclaimerLabel) => ({
    description: (
      <EditablePODisclaimerLabelTextFieldPresenter
        label={label}
        field='description'
      />
    ),
    text: (
      <EditablePODisclaimerLabelTextFieldPresenter label={label} field='text' />
    ),
  })

  return (
    <>
      <Space>
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space size='small'>
            <Header as='h3'>{intl.formatMessage(messages.heading)}</Header>
            <Text>
              {intl.formatMessage(messages.description, {
                optional: (
                  <Text bold inline>
                    {intl.formatMessage(messages.descriptionOptional)}
                  </Text>
                ),
              })}
            </Text>
          </Space>
          <PrimaryButton iconComponent={<IconAdd />} onClick={openCreateModal}>
            {intl.formatMessage(messages.newLabelButton)}
          </PrimaryButton>
        </Space>

        <PODisclaimerLabelListPresenter
          defaultId={defaultId}
          onToggleDefault={onToggleDefault}
          onDeleteLabel={onDeleteLabel}
          renderEditableCells={renderEditableCells}
        />
      </Space>

      {/* Create modal shell — owned by the Feature Set per ADR-9 */}
      <ModalDialog
        title={intl.formatMessage(messages.createModalTitle)}
        maxWidth='md'
        open={isCreateModalOpen}
        onClose={closeCreateModal}
      >
        {isCreateModalOpen && (
          <CreatePODisclaimerLabelFormPresenter
            existingLabels={labels}
            onSuccess={closeCreateModal}
            onCancel={closeCreateModal}
          />
        )}
      </ModalDialog>
    </>
  )
}

PODisclaimerLabelsView.displayName = 'PODisclaimerLabelsView'
